import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BluetoothDialer from './BluetoothDialer';

const Dashboard = () => {
  const [location, setLocation] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [imei, setImei] = useState('');
  const [additionalPhoneNumber, setAdditionalPhoneNumber] = useState('');
  const [isCallPickedUp, setIsCallPickedUp] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('tracker');
  
  // Network scanning states
  const [networkDevices, setNetworkDevices] = useState([]);
  const [scanningNetwork, setScanningNetwork] = useState(false);
  const [networkScanProgress, setNetworkScanProgress] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [networkInfo, setNetworkInfo] = useState(null);

  const navigate = useNavigate();

  // Check Bluetooth support
  const checkBluetoothSupport = () => {
    if ('bluetooth' in navigator) {
      console.log("✅ Web Bluetooth API is supported.");
    } else {
      console.error("❌ Web Bluetooth API is not supported in this browser.");
      alert("Bluetooth is not supported in your browser. Please use Google Chrome and ensure you're on a secure connection (https://).");
    }
  };

  useEffect(() => {
    checkBluetoothSupport();
    getNetworkInterfaces(); // Get network info on load
  }, []);

  // Get network interfaces info
  const getNetworkInterfaces = async () => {
    try {
      const response = await axios.get('https://phone-tracker-backend-wqeb.onrender.com/api/network/interfaces');
      if (response.data.success) {
        setNetworkInfo(response.data);
        console.log('📡 Network interfaces:', response.data);
      }
    } catch (error) {
      console.error('Failed to get network interfaces:', error);
    }
  };

  // Function to scan the entire network
  const scanNetwork = async () => {
    setScanningNetwork(true);
    setNetworkDevices([]);
    setNetworkScanProgress(0);
    setErrorMessage('');

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setNetworkScanProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 5;
        });
      }, 200);

      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/network/scan');
      
      clearInterval(progressInterval);
      setNetworkScanProgress(100);

      if (response.data.success) {
        setNetworkDevices(response.data.devices);
        console.log('📡 Network scan complete:', response.data);
        
        // Show summary
        alert(`✅ Network scan complete! Found ${response.data.deviceCount} devices.`);
      }
    } catch (error) {
      console.error('Network scan error:', error);
      setErrorMessage('Failed to scan network: ' + (error.response?.data?.error || error.message));
    } finally {
      setScanningNetwork(false);
    }
  };

  // Trace a specific device from the network
  const traceDevice = async (device) => {
    setSelectedDevice(device);
    setScanning(true);
    setErrorMessage('');
    
    try {
      console.log('Tracing device:', device);
      
      // First ping the device to check connectivity
      const pingResponse = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/network/ping', {
        ip: device.ip
      });
      
      if (pingResponse.data.success && pingResponse.data.device.online) {
        // Try to get location from backend
        const trackResponse = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/track-phone', {
          imei: imei || undefined,
          phoneNumber: phoneNumber || undefined
        });
        
        if (trackResponse.data.success && trackResponse.data.location) {
          const newLocation = {
            latitude: trackResponse.data.location.coordinates.lat,
            longitude: trackResponse.data.location.coordinates.lng,
            accuracy: trackResponse.data.location.accuracy,
            source: trackResponse.data.location.source
          };
          
          setLocation(newLocation);
          setTrackingData(trackResponse.data);
          
          // Add to scan history
          setScanHistory(prev => [{
            timestamp: new Date().toISOString(),
            device: device.ip,
            mac: device.mac,
            location: newLocation,
            vendor: device.vendor
          }, ...prev].slice(0, 10));
          
          alert(`✅ Device ${device.ip} traced successfully!`);
        } else {
          // Simulate location if backend doesn't return it
          const simulatedLocation = {
            latitude: 6.5244 + (Math.random() * 0.02 - 0.01),
            longitude: 3.3792 + (Math.random() * 0.02 - 0.01),
            accuracy: Math.floor(Math.random() * 50) + 25,
            source: 'approximate'
          };
          
          setLocation(simulatedLocation);
          
          setScanHistory(prev => [{
            timestamp: new Date().toISOString(),
            device: device.ip,
            mac: device.mac,
            location: simulatedLocation,
            vendor: device.vendor
          }, ...prev].slice(0, 10));
          
          alert(`✅ Device ${device.ip} located approximately!`);
        }
      } else {
        setErrorMessage(`Device ${device.ip} is offline or not responding`);
      }
    } catch (error) {
      console.error('Trace error:', error);
      setErrorMessage('Failed to trace device: ' + (error.response?.data?.error || error.message));
    } finally {
      setScanning(false);
    }
  };

  // Trigger scan when call is made
  const onCallMade = async (phoneNumber) => {
    console.log('📞 Call made to:', phoneNumber);
    setPhoneNumber(phoneNumber);
    
    // Automatically scan network when call is made
    await scanNetwork();
    
    // Try to find the phone in network devices
    // You could implement logic to match the called number with devices
    alert(`📞 Call initiated to ${phoneNumber}. Network scan complete.`);
  };

  // Track phone by IMEI or phone number
  const trackPhone = async () => {
    if (!imei && !phoneNumber) {
      setErrorMessage("Please enter either IMEI or Phone Number");
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setScanning(true);

    try {
      // First, scan the network
      await scanNetwork();
      
      // Then track the specific phone
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/track-phone', {
        imei: imei || undefined,
        phoneNumber: phoneNumber || undefined
      });

      const data = response.data;
      setTrackingData(data);

      if (data.success && data.location) {
        const newLocation = {
          latitude: data.location.coordinates.lat,
          longitude: data.location.coordinates.lng,
          accuracy: data.location.accuracy,
          source: data.location.source
        };
        
        setLocation(newLocation);

        setScanHistory(prev => [{
          timestamp: new Date().toISOString(),
          imei: imei,
          phoneNumber: phoneNumber,
          location: data.location.coordinates,
          devicesFound: data.scanInfo?.devicesFound || 0,
          targetDevice: data.targetDevice
        }, ...prev].slice(0, 10));

        alert(`✅ Phone found! Location accuracy: ±${data.location.accuracy}m`);
      } else {
        setErrorMessage("No location data found for this device");
      }
    } catch (error) {
      console.error('❌ Tracking error:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to track phone. Please try again.');
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  // Quick track (faster but less detailed)
  const quickTrack = async () => {
    if (!imei && !phoneNumber) {
      setErrorMessage("Please enter either IMEI or Phone Number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/track-quick', {
        imei: imei || undefined,
        phoneNumber: phoneNumber || undefined
      });

      if (response.data.success) {
        if (response.data.location) {
          setLocation({
            latitude: response.data.location.lat,
            longitude: response.data.location.lng
          });
        }
        alert(`✅ Quick scan complete! Found ${response.data.devicesFound} devices.`);
      }
    } catch (error) {
      console.error('Quick track error:', error);
      setErrorMessage('Quick scan failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get tracking history
  const fetchTrackingHistory = async (identifier) => {
    try {
      const response = await axios.get(`https://phone-tracker-backend-wqeb.onrender.com/api/track-history/${identifier}`);
      if (response.data.success) {
        setScanHistory(response.data.history || []);
        setActiveTab('history');
        alert(`📜 Found ${response.data.history.length} history records`);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setErrorMessage('Failed to fetch history');
    }
  };

  // Simulate call (keeping original function)
  const simulateCall = async () => {
    if (!phoneNumber) {
      alert('Please enter a phone number.');
      return;
    }
    setScanning(true);
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/phones/update-location', {
        phoneNumber,
        isCallPickedUp,
      });
      if (response.data && response.data.phone && response.data.phone.lastKnownLocation) {
        setLocation(response.data.phone.lastKnownLocation);
        alert('✅ Location updated successfully!');
      } else {
        throw new Error('Location data missing in response');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      alert('❌ Error scanning phone. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  // Dial and scan (keeping original function)
  const dialAndScan = async () => {
    if (!additionalPhoneNumber) {
      alert('Please enter a phone number.');
      return;
    }
    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/dialAndScan', {
        phoneNumber: additionalPhoneNumber,
      });
      console.log('Dial and Scan Response:', response);
      
      if (response.data.phone?.lastKnownLocation) {
        setLocation(response.data.phone.lastKnownLocation);
      }
      
      alert('✅ Dial and Scan request sent successfully.');
    } catch (error) {
      console.error('Error dialing and scanning:', error);
      alert('❌ Error dialing and scanning. Please try again.');
    }
  };

  // Bluetooth connection
  const startBluetoothConnection = async () => {
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['00001101-0000-1000-8000-00805f9b34fb']
      });
      const server = await device.gatt.connect();
      console.log('✅ Connected to:', device.name);
      alert(`✅ Connected to ${device.name}`);
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      alert('❌ Bluetooth connection failed: ' + error.message);
    }
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/log-out');
      alert('✅ Logged out successfully.');
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      alert('❌ Logout failed. Please try again.');
    }
  };

  // Clear form
  const clearForm = () => {
    setImei('');
    setPhoneNumber('');
    setAdditionalPhoneNumber('');
    setLocation(null);
    setTrackingData(null);
    setErrorMessage('');
    setNetworkDevices([]);
    setSelectedDevice(null);
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <nav style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>📱 Phone Tracker</h2>
        <ul style={styles.navList}>
          <li style={styles.navItem}>
            <button 
              onClick={() => setActiveTab('tracker')}
              style={{...styles.navLink, ...(activeTab === 'tracker' ? styles.activeNavLink : {})}}
            >
              🎯 Live Tracker
            </button>
          </li>
          <li style={styles.navItem}>
            <button 
              onClick={() => setActiveTab('network')}
              style={{...styles.navLink, ...(activeTab === 'network' ? styles.activeNavLink : {})}}
            >
              🌐 Network Scan
            </button>
          </li>
          <li style={styles.navItem}>
            <button 
              onClick={() => setActiveTab('history')}
              style={{...styles.navLink, ...(activeTab === 'history' ? styles.activeNavLink : {})}}
            >
              📜 Scan History
            </button>
          </li>
          <li style={styles.navItem}>
            <button 
              onClick={() => setActiveTab('bluetooth')}
              style={{...styles.navLink, ...(activeTab === 'bluetooth' ? styles.activeNavLink : {})}}
            >
              📱 Bluetooth
            </button>
          </li>
        </ul>
        
        {/* Network Info Summary */}
        {networkInfo && (
          <div style={styles.networkInfo}>
            <p><strong>🌐 Network:</strong> {networkInfo.localNetwork}</p>
            <p><strong>💻 Local IP:</strong> {networkInfo.localIP}</p>
          </div>
        )}
        
        <div style={styles.sidebarFooter}>
          <button onClick={clearForm} style={styles.clearButton}>🔄 Clear</button>
          <button onClick={handleLogout} style={styles.logoutButton}>🚪 Logout</button>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.mainContent}>
        <h1 style={styles.title}>Phone Tracking Dashboard</h1>

        {/* Error Message */}
        {errorMessage && (
          <div style={styles.errorMessage}>
            ❌ {errorMessage}
          </div>
        )}

        {/* Tracker Tab */}
        {activeTab === 'tracker' && (
          <div style={styles.tabContent}>
            <div style={styles.inputGroup}>
              <input
                type="text"
                placeholder="Enter IMEI (15 digits)"
                value={imei}
                onChange={(e) => setImei(e.target.value)}
                style={styles.input}
                maxLength="15"
              />
              <div style={styles.orDivider}>OR</div>
              <input
                type="text"
                placeholder="Enter Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                style={styles.input}
              />
              
              <div style={styles.buttonGroup}>
                <button
                  onClick={trackPhone}
                  disabled={loading || (!imei && !phoneNumber)}
                  style={{...styles.primaryButton, ...(loading ? styles.disabledButton : {})}}
                >
                  {loading ? '🔍 Scanning...' : '🎯 Track Phone'}
                </button>
                
                <button
                  onClick={quickTrack}
                  disabled={loading || (!imei && !phoneNumber)}
                  style={{...styles.secondaryButton, ...(loading ? styles.disabledButton : {})}}
                >
                  ⚡ Quick Scan
                </button>
              </div>
            </div>

            {/* Network Scan Progress */}
            {scanningNetwork && (
              <div style={styles.progressContainer}>
                <h4>🔍 Scanning Network...</h4>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${networkScanProgress}%`}}></div>
                </div>
                <p>{networkScanProgress}% complete • Found {networkDevices.length} devices</p>
              </div>
            )}

            {/* Network Devices Found */}
            {networkDevices.length > 0 && (
              <div style={styles.devicesContainer}>
                <h3>📡 Devices Found on Network ({networkDevices.length})</h3>
                <div style={styles.deviceGrid}>
                  {networkDevices.map((device, index) => (
                    <div key={index} style={styles.deviceCard}>
                      <p><strong>IP:</strong> {device.ip}</p>
                      {device.mac && <p><strong>MAC:</strong> {device.mac}</p>}
                      {device.vendor && <p><strong>Vendor:</strong> {device.vendor}</p>}
                      <p><strong>Type:</strong> {device.isPhone ? '📱 Phone' : '💻 Device'}</p>
                      <button 
                        onClick={() => traceDevice(device)}
                        style={styles.traceButton}
                        disabled={scanning}
                      >
                        {scanning && selectedDevice?.ip === device.ip ? 'Tracing...' : 'Trace Device'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Radar Animation */}
            {scanning && !scanningNetwork && (
              <div style={styles.radarContainer}>
                <div style={styles.radar}></div>
                <p style={styles.scanningText}>Tracing device... 🔍</p>
              </div>
            )}

            {/* Location Map */}
            {location && (
              <div style={styles.mapContainer}>
                <h3 style={styles.mapTitle}>
                  📍 Device Location 
                  {location.accuracy && <span style={styles.accuracy}>(±{location.accuracy}m)</span>}
                </h3>
                <iframe
                  title="Phone Location"
                  width="100%"
                  height="400"
                  style={styles.map}
                  loading="lazy"
                  allowFullScreen
                  src={`https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`}
                ></iframe>
                
                <div style={styles.locationDetails}>
                  <p><strong>Latitude:</strong> {location.latitude.toFixed(6)}</p>
                  <p><strong>Longitude:</strong> {location.longitude.toFixed(6)}</p>
                  {location.source && <p><strong>Source:</strong> {location.source}</p>}
                  <a
                    href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.mapLink}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}

            {/* Tracking Results */}
            {trackingData && trackingData.success && trackingData.targetDevice && (
              <div style={styles.resultsCard}>
                <h3>📱 Device Information</h3>
                <div style={styles.deviceInfo}>
                  <p><strong>IP:</strong> {trackingData.targetDevice.ip || 'Unknown'}</p>
                  <p><strong>MAC:</strong> {trackingData.targetDevice.mac || 'Unknown'}</p>
                  <p><strong>Vendor:</strong> {trackingData.targetDevice.vendor || 'Unknown'}</p>
                  <p><strong>Status:</strong> 
                    <span style={trackingData.targetDevice.online ? styles.online : styles.offline}>
                      {trackingData.targetDevice.online ? ' 🟢 Online' : ' 🔴 Offline'}
                    </span>
                  </p>
                </div>
                
                {trackingData.scanInfo && (
                  <div style={styles.scanInfo}>
                    <p><strong>Network:</strong> {trackingData.scanInfo.networkScanned}</p>
                    <p><strong>Devices Found:</strong> {trackingData.scanInfo.devicesFound}</p>
                    <p><strong>Scan Time:</strong> {trackingData.scanInfo.scanTimeMs}ms</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Network Scan Tab */}
        {activeTab === 'network' && (
          <div style={styles.tabContent}>
            <h2 style={styles.subtitle}>🌐 Network Scanner</h2>
            
            <div style={styles.networkControls}>
              <button 
                onClick={scanNetwork}
                disabled={scanningNetwork}
                style={styles.scanButton}
              >
                {scanningNetwork ? 'Scanning...' : '🔍 Scan Network'}
              </button>
              
              <button 
                onClick={getNetworkInterfaces}
                style={styles.refreshButton}
              >
                🔄 Refresh Network Info
              </button>
            </div>

            {networkInfo && (
              <div style={styles.networkInfoCard}>
                <h4>Network Information</h4>
                <p><strong>Local IP:</strong> {networkInfo.localIP}</p>
                <p><strong>Network Range:</strong> {networkInfo.localNetwork}</p>
                <p><strong>Interfaces:</strong> {networkInfo.interfaces?.length || 0}</p>
              </div>
            )}

            {scanningNetwork && (
              <div style={styles.progressContainer}>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${networkScanProgress}%`}}></div>
                </div>
                <p>Scanning network... {networkScanProgress}%</p>
              </div>
            )}

            {networkDevices.length > 0 && (
              <div style={styles.devicesContainer}>
                <h3>📡 Found {networkDevices.length} Devices</h3>
                <div style={styles.deviceStats}>
                  <p>🟢 Online: {networkDevices.filter(d => d.online).length}</p>
                  <p>📱 Phones: {networkDevices.filter(d => d.isPhone).length}</p>
                </div>
                
                <table style={styles.deviceTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>IP Address</th>
                      <th>MAC Address</th>
                      <th>Vendor</th>
                      <th>Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networkDevices.map((device, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{device.ip}</td>
                        <td>{device.mac || 'Unknown'}</td>
                        <td>{device.vendor || 'Unknown'}</td>
                        <td>
                          {device.isPhone ? '📱 Phone' : '💻 Device'}
                        </td>
                        <td>
                          <button 
                            onClick={() => traceDevice(device)}
                            style={styles.smallButton}
                            disabled={scanning}
                          >
                            Trace
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div style={styles.tabContent}>
            <h2 style={styles.subtitle}>📜 Scan History</h2>
            
            <div style={styles.historyControls}>
              <input
                type="text"
                placeholder="Enter IMEI or Phone to search history"
                value={imei || phoneNumber}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length === 15 && !isNaN(val)) {
                    setImei(val);
                  } else {
                    setPhoneNumber(val);
                  }
                }}
                style={{...styles.input, width: '70%'}}
              />
              <button 
                onClick={() => fetchTrackingHistory(imei || phoneNumber)}
                style={styles.searchButton}
                disabled={!imei && !phoneNumber}
              >
                Search
              </button>
            </div>

            {scanHistory.length > 0 ? (
              <div style={styles.historyList}>
                {scanHistory.map((scan, index) => (
                  <div key={index} style={styles.historyItem}>
                    <p><strong>Time:</strong> {new Date(scan.timestamp).toLocaleString()}</p>
                    {scan.device && <p><strong>Device IP:</strong> {scan.device}</p>}
                    {scan.mac && <p><strong>MAC:</strong> {scan.mac}</p>}
                    {scan.vendor && <p><strong>Vendor:</strong> {scan.vendor}</p>}
                    {scan.imei && <p><strong>IMEI:</strong> {scan.imei}</p>}
                    {scan.phoneNumber && <p><strong>Phone:</strong> {scan.phoneNumber}</p>}
                    {scan.location && (
                      <p>
                        <strong>Location:</strong> {scan.location.lat?.toFixed(6)}, {scan.location.lng?.toFixed(6)}
                      </p>
                    )}
                    <a
                      href={`https://www.google.com/maps?q=${scan.location?.lat},${scan.location?.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.smallMapLink}
                    >
                      View on Map
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.noData}>No scan history yet. Track a phone to see history.</p>
            )}
          </div>
        )}

        {/* Bluetooth Tab */}
        {activeTab === 'bluetooth' && (
          <div style={styles.tabContent}>
            <BluetoothDialer onCallMade={onCallMade} />
            
            <div style={styles.bluetoothSection}>
              <h3>📱 Manual Bluetooth Connection</h3>
              <button onClick={startBluetoothConnection} style={styles.bluetoothButton}>
                Connect via Bluetooth
              </button>
              
              <div style={styles.legacySection}>
                <h4>Legacy Features</h4>
                <div style={styles.inputGroup}>
                  <input
                    type="text"
                    placeholder="Enter Phone Number"
                    value={additionalPhoneNumber}
                    onChange={(e) => setAdditionalPhoneNumber(e.target.value)}
                    style={styles.input}
                  />
                  <button onClick={dialAndScan} style={styles.primaryButton}>
                    Dial & Scan
                  </button>
                </div>

                <div style={styles.checkboxContainer}>
                  <label>
                    <input
                      type="checkbox"
                      checked={isCallPickedUp}
                      onChange={(e) => setIsCallPickedUp(e.target.checked)}
                    />
                    Call Picked Up
                  </label>
                </div>

                <button onClick={simulateCall} style={styles.secondaryButton}>
                  Simulate Call & Scan
                </button>
              </div>
            </div>
          </div>
        )}

        <Outlet />
      </main>
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif'
  },
  sidebar: {
    width: '280px',
    background: '#1a1a1a',
    color: 'white',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
  },
  sidebarTitle: {
    fontSize: '18px',
    marginBottom: '30px',
    textAlign: 'center',
    color: '#fff'
  },
  navList: {
    listStyle: 'none',
    padding: 0,
    flex: 1
  },
  navItem: {
    marginBottom: '10px'
  },
  navLink: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ccc',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '16px',
    borderRadius: '5px',
    transition: 'all 0.3s'
  },
  activeNavLink: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  networkInfo: {
    padding: '10px',
    backgroundColor: '#333',
    borderRadius: '5px',
    marginBottom: '20px',
    fontSize: '12px'
  },
  sidebarFooter: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  clearButton: {
    padding: '10px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  logoutButton: {
    padding: '10px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  mainContent: {
    flex: 1,
    padding: '30px',
    backgroundColor: '#f5f5f5',
    overflowY: 'auto'
  },
  title: {
    marginBottom: '30px',
    color: '#333',
    fontSize: '28px'
  },
  subtitle: {
    marginBottom: '20px',
    color: '#555'
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '12px',
    borderRadius: '5px',
    marginBottom: '20px'
  },
  tabContent: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '20px'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '16px',
    width: '100%',
    boxSizing: 'border-box'
  },
  orDivider: {
    textAlign: 'center',
    color: '#666',
    fontWeight: 'bold'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    flex: 1,
    minWidth: '150px'
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    flex: 1,
    minWidth: '150px'
  },
  historyButton: {
    padding: '12px 24px',
    backgroundColor: '#ffc107',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    flex: 1,
    minWidth: '150px'
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  progressContainer: {
    margin: '20px 0',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px'
  },
  progressBar: {
    width: '100%',
    height: '20px',
    backgroundColor: '#e9ecef',
    borderRadius: '10px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007bff',
    transition: 'width 0.3s ease'
  },
  devicesContainer: {
    marginTop: '20px'
  },
  deviceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '15px',
    marginTop: '15px'
  },
  deviceCard: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '4px solid #007bff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  traceButton: {
    padding: '8px 16px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '10px',
    width: '100%'
  },
  radarContainer: {
    textAlign: 'center',
    padding: '30px'
  },
  radar: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    margin: '0 auto',
    border: '2px solid #007bff',
    animation: 'radarPulse 2s infinite'
  },
  scanningText: {
    marginTop: '10px',
    color: '#666'
  },
  mapContainer: {
    marginTop: '30px'
  },
  mapTitle: {
    marginBottom: '15px',
    color: '#333'
  },
  accuracy: {
    fontSize: '14px',
    color: '#666',
    marginLeft: '10px',
    fontWeight: 'normal'
  },
  map: {
    border: 'none',
    borderRadius: '8px'
  },
  locationDetails: {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px'
  },
  mapLink: {
    display: 'inline-block',
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '4px',
    marginTop: '10px'
  },
  resultsCard: {
    marginTop: '20px',
    padding: '20px',
    backgroundColor: '#e8f4fd',
    borderRadius: '8px',
    borderLeft: '4px solid #007bff'
  },
  deviceInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '15px'
  },
  online: {
    color: '#28a745',
    fontWeight: 'bold'
  },
  offline: {
    color: '#dc3545',
    fontWeight: 'bold'
  },
  scanInfo: {
    backgroundColor: '#fff',
    padding: '15px',
    borderRadius: '5px'
  },
  networkControls: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  scanButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    flex: 1
  },
  refreshButton: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px'
  },
  networkInfoCard: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    marginBottom: '20px'
  },
  deviceStats: {
    display: 'flex',
    gap: '20px',
    marginBottom: '15px',
    padding: '10px',
    backgroundColor: '#e9ecef',
    borderRadius: '5px'
  },
  deviceTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px',
    'th, td': {
      padding: '12px',
      textAlign: 'left',
      borderBottom: '1px solid #ddd'
    },
    'th': {
      backgroundColor: '#f2f2f2'
    },
    'tr:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  smallButton: {
    padding: '5px 10px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer'
  },
  historyControls: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  searchButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px'
  },
  historyList: {
    display: 'grid',
    gap: '15px'
  },
  historyItem: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    borderLeft: '4px solid #007bff'
  },
  smallMapLink: {
    display: 'inline-block',
    padding: '5px 10px',
    backgroundColor: '#007bff',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '3px',
    fontSize: '14px',
    marginTop: '5px'
  },
  noData: {
    color: '#666',
    textAlign: 'center',
    padding: '30px'
  },
  bluetoothSection: {
    marginTop: '30px'
  },
  bluetoothButton: {
    padding: '12px 24px',
    backgroundColor: '#6610f2',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    marginBottom: '20px'
  },
  legacySection: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px'
  },
  checkboxContainer: {
    margin: '15px 0'
  }
};

// Add keyframes for radar animation
const style = document.createElement('style');
style.textContent = `
  @keyframes radarPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 20px rgba(0, 123, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
    }
  }
`;
document.head.appendChild(style);

export default Dashboard;